import type { Getter, Setter } from 'jotai';
import type { UndoRedoActionCommand } from '../../../../../modernization-common/undo-redo/types';
import { ANNOTATION_ML_STATE, ANNOTATION_TYPE } from '../../../../labelling/constants';
import { boundCoordinate } from '../../../../labelling/reducers/actionReducers/annotations/utils/bound-coordinate';
import type { AnnotationStoreT, AnnotationT, BrushAnnotationT } from '../../../../labelling/types';
import { applyAnnotationUIState } from '../../../utils/apply-annotation-ui-state';
import { annotationFramesAtom, consensusAnnotationFramesAtom, mlAnnotationFramesAtom, mlAnnotationUIStateAtom } from '../../annotation-atoms';
import { metadataAtom } from '../../file-atoms';
import type { AnnotationFrameWriteEntry } from '../../studio-store-write/actions';
import { STUDIO_WRITE_ACTIONS } from '../../studio-store-write/actions';
import { dispatchStudioStoreWriteAtom } from '../../studio-store-write/dispatch-studio-store-write';
import type { ImportAnnotationsEventPayload } from '../events';
export const importAnnotationsCommandFactory = (get: Getter, set: Setter): UndoRedoActionCommand<ImportAnnotationsEventPayload, void> => {
  let addedAnnotationIds: string[] = [];
  let hiddenMlAnnotationIds: string[] = [];
  let hiddenConsensusAnnotationIds: string[] = [];
  let updatedSegmentationAnnotations: AnnotationStoreT = {};
  // Tracks existingSegId → ML answers when a merge target has no existing answers.
  let mergedSegmentationAnswers: Record<string, AnnotationT['metadata']> = {};
  return {
    execute: payload => {
      const {
        frameIndex,
        annotationIdList
      } = payload;
      addedAnnotationIds = [];
      hiddenMlAnnotationIds = [];
      hiddenConsensusAnnotationIds = [];
      updatedSegmentationAnnotations = {};
      mergedSegmentationAnswers = {};
      const frames = get(annotationFramesAtom);
      const frame = frames[frameIndex];
      if (!frame) {
        return;
      }
      const metadata = get(metadataAtom);
      // Mutable lookup used to detect existing segmentation annotations during iteration
      const annotationStoreLookup: AnnotationStoreT = {
        ...frame.annotationStore
      };
      const mlFrames = get(mlAnnotationFramesAtom);
      const consensusFrames = get(consensusAnnotationFramesAtom);
      let annotationMLStore: AnnotationStoreT | undefined = mlFrames.get(frameIndex) ? {
        ...mlFrames.get(frameIndex)!
      } : undefined;
      let annotationConsensusStore: AnnotationStoreT | undefined = consensusFrames.get(frameIndex) ? {
        ...consensusFrames.get(frameIndex)!
      } : undefined;
      const writes: AnnotationFrameWriteEntry[] = [];
      for (const annotationId of annotationIdList) {
        let annotationData: AnnotationT | undefined = annotationMLStore?.[annotationId];
        let isFromConsensusStore = false;
        if (!annotationData && annotationConsensusStore) {
          annotationData = annotationConsensusStore[annotationId];
          isFromConsensusStore = true;
        }
        if (!annotationData) continue;
        if (isFromConsensusStore) {
          annotationConsensusStore = {
            ...annotationConsensusStore,
            [annotationId]: {
              ...annotationConsensusStore![annotationId]!,
              hidden: true
            }
          };
          hiddenConsensusAnnotationIds.push(annotationId);
        } else {
          hiddenMlAnnotationIds.push(annotationId);
        }
        const annotationWithFixedBounds = boundCoordinate(annotationData, metadata);
        if (annotationData.annotationType === ANNOTATION_TYPE.SEGMENTATION) {
          const existingSegId = findSegmentationAnnotationId(annotationStoreLookup, annotationData.annotationLabel.title);
          if (existingSegId) {
            const existingAnnotation = annotationStoreLookup[existingSegId]!;
            updatedSegmentationAnnotations[existingSegId] = existingAnnotation;
            const merged = mergeSegmentationAnnotation((existingAnnotation as BrushAnnotationT), (annotationWithFixedBounds as BrushAnnotationT));
            annotationStoreLookup[existingSegId] = merged;
            writes.push({
              frameIndex,
              annotationId: existingSegId,
              annotation: merged
            });

            // If the existing annotation has no answers yet, carry over the ML answers.
            // Don't overwrite user-provided answers already on the existing annotation.
            const existingAnswers = existingAnnotation.metadata?.answers;
            const incomingAnswers = annotationWithFixedBounds.metadata?.answers;
            if ((!existingAnswers || Object.keys(existingAnswers).length === 0) && incomingAnswers && Object.keys(incomingAnswers).length > 0) {
              mergedSegmentationAnswers[existingSegId] = annotationWithFixedBounds.metadata;
            }
            continue;
          }
        }
        const newAnnotation: AnnotationT = {
          ...annotationWithFixedBounds,
          hidden: false,
          metadata: {
            ...annotationWithFixedBounds.metadata,
            ...(isFromConsensusStore ? {} : {
              mlData: {
                ...(annotationWithFixedBounds.metadata?.mlData ?? {}),
                state: ANNOTATION_ML_STATE.AUTOMATED
              }
            })
          }
        };
        annotationStoreLookup[annotationId] = newAnnotation;
        writes.push({
          frameIndex,
          annotationId,
          annotation: newAnnotation
        });
        addedAnnotationIds.push(annotationId);
      }
      if (writes.length > 0) {
        set(dispatchStudioStoreWriteAtom, {
          type: STUDIO_WRITE_ACTIONS.BULK_WRITE,
          writes
        });
      }
      for (const annotationId of addedAnnotationIds) {
        const annotation = annotationStoreLookup[annotationId];
        const answers = annotation?.metadata?.answers;
        if (answers && Object.keys(answers).length > 0) {
          set(dispatchStudioStoreWriteAtom, {
            type: STUDIO_WRITE_ACTIONS.UPDATE_METADATA,
            annotationId,
            partialPayload: {
              answers
            }
          });
        }
      }
      for (const [annotationId, metadata] of Object.entries(mergedSegmentationAnswers)) {
        const answers = metadata?.answers;
        if (answers && Object.keys(answers).length > 0) {
          set(dispatchStudioStoreWriteAtom, {
            type: STUDIO_WRITE_ACTIONS.UPDATE_METADATA,
            annotationId,
            partialPayload: {
              answers
            }
          });
        }
      }
      const newMLMap = new Map(get(mlAnnotationFramesAtom));
      if (annotationMLStore) {
        newMLMap.set(frameIndex, annotationMLStore);
      }
      set(mlAnnotationFramesAtom, newMLMap);
      const newConsensusMap = new Map(get(consensusAnnotationFramesAtom));
      if (annotationConsensusStore) {
        newConsensusMap.set(frameIndex, annotationConsensusStore);
      }
      set(consensusAnnotationFramesAtom, newConsensusMap);
      if (hiddenMlAnnotationIds.length > 0) {
        set(mlAnnotationUIStateAtom, prev => applyAnnotationUIState(prev, hiddenMlAnnotationIds, {
          hidden: true
        }));
      }
    },
    undo: payload => {
      const {
        frameIndex
      } = payload;
      if (addedAnnotationIds.length > 0) {
        set(dispatchStudioStoreWriteAtom, {
          type: STUDIO_WRITE_ACTIONS.BULK_DELETE,
          deletes: addedAnnotationIds.map(annotationId => ({
            frameIndex,
            annotationId
          }))
        });
      }
      const segWrites = Object.entries(updatedSegmentationAnnotations);
      if (segWrites.length > 0) {
        set(dispatchStudioStoreWriteAtom, {
          type: STUDIO_WRITE_ACTIONS.BULK_WRITE,
          writes: segWrites.map(([annotationId, annotation]) => ({
            frameIndex,
            annotationId,
            annotation
          }))
        });
      }
      const mlFrames = get(mlAnnotationFramesAtom);
      const restoredMLStore: AnnotationStoreT = mlFrames.get(frameIndex) ? {
        ...mlFrames.get(frameIndex)!
      } : {};
      for (const id of hiddenMlAnnotationIds) {
        set(mlAnnotationUIStateAtom, prev => applyAnnotationUIState(prev, [id], {
          hidden: false
        }));
      }
      const consensusFrames = get(consensusAnnotationFramesAtom);
      const restoredConsensusStore: AnnotationStoreT = consensusFrames.get(frameIndex) ? {
        ...consensusFrames.get(frameIndex)!
      } : {};
      for (const id of hiddenConsensusAnnotationIds) {
        const ann = restoredConsensusStore[id];
        if (ann) {
          restoredConsensusStore[id] = {
            ...ann,
            hidden: false
          };
        }
      }
      const newMLMap = new Map(mlFrames);
      if (Object.keys(restoredMLStore).length > 0) {
        newMLMap.set(frameIndex, restoredMLStore);
      }
      set(mlAnnotationFramesAtom, newMLMap);
      const newConsensusMap = new Map(consensusFrames);
      if (Object.keys(restoredConsensusStore).length > 0) {
        newConsensusMap.set(frameIndex, restoredConsensusStore);
      }
      set(consensusAnnotationFramesAtom, newConsensusMap);
    }
  };
};
const findSegmentationAnnotationId = (annotationStore: AnnotationStoreT, labelTitle: string): string | undefined => {
  return Object.keys(annotationStore).find(id => {
    const ann = annotationStore[id];
    return ann?.annotationType === ANNOTATION_TYPE.SEGMENTATION && ann.annotationLabel.title === labelTitle;
  });
};
const mergeSegmentationAnnotation = (existing: BrushAnnotationT, incoming: BrushAnnotationT): BrushAnnotationT => {
  return {
    ...existing,
    data: {
      ...existing.data,
      points: [...(existing.data.points ?? []), ...(incoming.data.points ?? [])]
    },
    metadata: {
      ...existing.metadata,
      mlData: {
        ...(existing.metadata?.mlData ?? {}),
        state: ANNOTATION_ML_STATE.ASSISTED
      }
    }
  };
};